import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type JSX, type ReactNode } from 'react';
import type { Achievement, Quest } from '@yuan-math/learning';

/**
 * Celebration.
 *
 * Everything the platform wants to say "well done" about — a badge, a quest, a
 * level, a concept crossing the mastery line — arrives through one channel and
 * is shown in one place. That matters for two reasons beyond tidiness:
 *
 * 1. **It can be rationed.** A single queue can hold back a third toast while
 *    two are on screen, so finishing a set never buries the mathematics under a
 *    stack of confetti.
 * 2. **It is announced once.** The region is a single `aria-live` container, so
 *    a screen reader hears "quest complete" rather than a page re-reading
 *    itself.
 *
 * The rules that decide *whether* something is earned all live in
 * `@yuan-math/learning`; this file only knows how to congratulate.
 */

export type RewardTone = 'achievement' | 'quest' | 'level' | 'mastery' | 'streak';

export type Reward = {
  /** Stable within a session; used as the React key and for de-duplication. */
  key: string;
  tone: RewardTone;
  icon: string;
  title: string;
  detail?: string;
  /** XP paid for this, when there was any. */
  xp?: number;
};

type RewardsValue = {
  celebrate(rewards: Reward | Reward[]): void;
  dismiss(key: string): void;
  visible: Reward[];
};

/** How long a toast stays before it retires itself. */
const DWELL_MS = 6000;

/** More than this on screen at once is noise, not encouragement. */
const MAX_VISIBLE = 3;

/**
 * A no-op default rather than a thrown error.
 *
 * A page rendered on its own in a test has nothing to celebrate into, and that
 * is not a bug worth crashing a lesson over.
 */
const RewardsContext = createContext<RewardsValue>({
  celebrate: () => undefined,
  dismiss: () => undefined,
  visible: [],
});

export function RewardsProvider({ children }: { children: ReactNode }): JSX.Element {
  const [visible, setVisible] = useState<Reward[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((key: string) => {
    const timer = timers.current.get(key);
    if (timer) clearTimeout(timer);
    timers.current.delete(key);
    setVisible((current) => current.filter((reward) => reward.key !== key));
  }, []);

  const celebrate = useCallback(
    (rewards: Reward | Reward[]) => {
      const incoming = Array.isArray(rewards) ? rewards : [rewards];
      if (incoming.length === 0) return;

      setVisible((current) => {
        const known = new Set(current.map((reward) => reward.key));
        const added = incoming.filter((reward) => !known.has(reward.key));
        if (added.length === 0) return current;

        for (const reward of added) {
          timers.current.set(
            reward.key,
            setTimeout(() => dismiss(reward.key), DWELL_MS),
          );
        }
        // Newest first, oldest pushed off the end: the thing that just happened
        // is the thing worth reading.
        return [...added.reverse(), ...current].slice(0, MAX_VISIBLE);
      });
    },
    [dismiss],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const value = useMemo<RewardsValue>(() => ({ celebrate, dismiss, visible }), [celebrate, dismiss, visible]);

  return (
    <RewardsContext.Provider value={value}>
      {children}
      <RewardToasts rewards={visible} onDismiss={dismiss} />
    </RewardsContext.Provider>
  );
}

export function useRewards(): RewardsValue {
  return useContext(RewardsContext);
}

function RewardToasts({ rewards, onDismiss }: { rewards: Reward[]; onDismiss(key: string): void }): JSX.Element {
  return (
    <div className="reward-toasts" role="status" aria-live="polite">
      {rewards.map((reward) => (
        <div key={reward.key} className={`reward-toast reward-toast--${reward.tone}`}>
          <span className="reward-toast__icon" aria-hidden="true">
            {reward.icon}
          </span>
          <div className="reward-toast__body">
            <p className="reward-toast__title">{reward.title}</p>
            {reward.detail ? <p className="reward-toast__detail">{reward.detail}</p> : null}
          </div>
          {reward.xp ? <span className="reward-toast__xp">+{reward.xp} XP</span> : null}
          <button
            type="button"
            className="reward-toast__close"
            onClick={() => onDismiss(reward.key)}
            aria-label={`Dismiss: ${reward.title}`}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Turning an outcome into congratulations                                    */
/* -------------------------------------------------------------------------- */

export type RewardSource = {
  unlockedAchievements?: Achievement[];
  completedQuests?: Quest[];
  sweptTheDay?: boolean;
  leveledUpTo?: number;
  levelTitle?: string;
  justMastered?: boolean;
  masteredConceptTitle?: string;
};

/**
 * The toasts one reducer outcome deserves.
 *
 * Note what is *not* here: XP for an ordinary answer. Every correct answer
 * earning a popup trains a student to wait for the popup, and the number is
 * already on screen. Only things that happen rarely are worth interrupting for.
 */
export function rewardsFor(source: RewardSource, stamp: number): Reward[] {
  const rewards: Reward[] = [];

  if (source.justMastered) {
    rewards.push({
      key: `mastery-${source.masteredConceptTitle ?? ''}-${stamp}`,
      tone: 'mastery',
      icon: '🔑',
      title: 'Mastered',
      detail: source.masteredConceptTitle
        ? `${source.masteredConceptTitle} is yours. It will come back for review, not for teaching.`
        : 'That concept is yours.',
    });
  }

  for (const quest of source.completedQuests ?? []) {
    rewards.push({
      key: `quest-${quest.id}-${stamp}`,
      tone: 'quest',
      icon: quest.icon,
      title: `Quest complete — ${quest.title}`,
      detail: quest.description,
      xp: quest.xp,
    });
  }

  if (source.sweptTheDay) {
    rewards.push({
      key: `sweep-${stamp}`,
      tone: 'quest',
      icon: '🌟',
      title: 'Every quest done today',
      detail: 'Come back tomorrow for three more.',
    });
  }

  if (source.leveledUpTo !== undefined) {
    rewards.push({
      key: `level-${source.leveledUpTo}`,
      tone: 'level',
      icon: '⭐',
      title: `Level ${source.leveledUpTo}`,
      detail: source.levelTitle ? `You are now a ${source.levelTitle.toLowerCase()}.` : undefined,
    });
  }

  for (const achievement of source.unlockedAchievements ?? []) {
    rewards.push({
      key: `badge-${achievement.id}`,
      tone: 'achievement',
      icon: achievement.icon,
      title: achievement.title,
      detail: achievement.description,
    });
  }

  return rewards;
}
